import { z } from 'zod';
import isCreditCard from '@/lib/isCreditCard';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Spinner } from '../ui/spinner';
import {
  handleCreditCardNumberChange,
  handleCreditCardNumberKeyDown,
  handleNumberDigitChange,
  handleNumberKeyDown,
} from '@/lib/CreditCardUtils';
import { useEffect, useState } from 'react';
import { useToast } from '../ui/use-toast';
import { Checkbox } from '../ui/checkbox';
import PrivacyDialog from '../PrivacyDialog';

export default function MasterpassCardForm({
  open,
  onOpenChange,
  fullnamePreset,
  addingCard,
  onSubmit,
  forPayment,
  userIsAnonymous,
  setPaying,
  paying,
  paymentActionLabel,
  onCardNumberChange,
  checkoutB,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fullnamePreset: string;
  addingCard: boolean;
  onSubmit: (values: z.infer<any>) => void;
  forPayment: boolean;
  userIsAnonymous: boolean;
  setPaying: (loading: boolean) => void;
  paying?: boolean;
  paymentActionLabel?: string;
  onCardNumberChange?: (cardNumber: string) => void;
  checkoutB?: boolean;
}) {
  const { toast } = useToast();

  const [isPrivacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  function isExpirationDateValid(expirationDate: string): boolean {
    const [currentMonth, currentYear] = new Date()
      .toLocaleDateString('en', { month: '2-digit', year: '2-digit' })
      .split('/')
      .map((i) => parseInt(i));
    const [month, year] = expirationDate.split('/').map((i) => parseInt(i));
    if (month < 1 || month > 12) return false;
    return year > currentYear || (year === currentYear && month >= currentMonth);
  }

  const fieldsCheck = {
    fullname: z.string().min(3, {
      message: 'Lütfen isim bilgisini giriniz',
    }),
    cardAlias: forPayment // && userIsAnonymous
      ? z.string().optional().or(z.literal(''))
      : z.string().min(3, {
          message: 'Lütfen kart ismi giriniz.',
        }),
    cardNumber: z.string().refine((val) => isCreditCard(val), {
      message: 'Lütfen geçerli bir kredi kartı numarası giriniz',
    }),
    expirationMonth: z.string().regex(/^\d{2}/, 'Lütfen ay alanını doldurunuz'),
    expirationYear: z.string().regex(/^\d{2}/, 'Lütfen yıl alanını doldurunuz'),
    cvv: z.string().regex(/^\d{3}$/, 'Lütfen 3 hane CVV giriniz'),
    privacyCheck: z.boolean().refine((val) => val === true, {
      message: 'Lütfen gizlilik sözleşmesini kabul ediniz',
    }),
    termsCheck: userIsAnonymous
      ? z.boolean().optional().or(z.literal(''))
      : z.boolean().refine((val) => val === true, {
          message: 'Lütfen kullanım koşullarını kabul ediniz',
        }),
  };

  const FormSchema = z
    .object(fieldsCheck)
    .refine(
      (data) =>
        /^\d{2}\/\d{2}$/.test(data.expirationMonth + '/' + data.expirationYear) &&
        isExpirationDateValid(data.expirationMonth + '/' + data.expirationYear),
      {
        message: 'Geçersiz veya eski bir tarih',
        path: ['expirationYear'],
      },
    );

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      fullname: fullnamePreset,
      cardAlias: '',
      cardNumber: '',
      expirationMonth: '',
      expirationYear: '',
      cvv: '',
      privacyCheck: false,
      termsCheck: Boolean(checkoutB),
    },
  });

  useEffect(() => {
    form.reset();
  }, [open]);

  const aError = () => {
    toast({
      variant: 'destructive',
      description: 'Bir hata oluştu',
    });
  };

  /* {(!forPayment || !userIsAnonymous) && ( */

  if (checkoutB && forPayment) {
    const now = new Date().getFullYear();
    const years = Array.from({ length: 16 }, (_, index) => now + index);
    const fieldClass =
      'h-12 w-full rounded-xl border border-[#E7E4DC] bg-white px-3 text-base outline-none placeholder:text-[#B0B0B0]';
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 rounded-2xl border border-[#E7E4DC] bg-white p-4">
            <FormField
              control={form.control}
              name="fullname"
              render={({ field }) => (
                <FormItem>
                  <span className="text-sm font-medium text-[#212121]">Kart Sahibi</span>
                  <FormControl>
                    <Input placeholder="Ad Soyad" className={fieldClass} {...field} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="cardNumber"
              render={({ field }) => (
                <FormItem>
                  <span className="text-sm font-medium text-[#212121]">Kart Numarası</span>
                  <FormControl>
                    <Input
                      type="tel"
                      value={field.value}
                      placeholder="5555 5555 5555 5555"
                      onChange={(event) => {
                        handleCreditCardNumberChange((value) => {
                          field.onChange(value);
                          onCardNumberChange?.(value);
                        }, event);
                      }}
                      onKeyDown={(event) => handleCreditCardNumberKeyDown(field.onChange, event)}
                      className={fieldClass}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <div>
              <p className="text-sm font-medium text-[#212121]">Son kullanma Tarihi</p>
              <div className="mt-2 grid grid-cols-1 gap-2 min-[380px]:grid-cols-[1fr_1fr_0.8fr]">
                <FormField
                  control={form.control}
                  name="expirationMonth"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <select
                          value={field.value}
                          onChange={(event) => field.onChange(event.target.value)}
                          className={fieldClass}
                        >
                          <option value="">Ay</option>
                          {Array.from({ length: 12 }, (_, index) =>
                            String(index + 1).padStart(2, '0'),
                          ).map((month) => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="expirationYear"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <select
                          value={field.value}
                          onChange={(event) => field.onChange(event.target.value)}
                          className={fieldClass}
                        >
                          <option value="">Yıl</option>
                          {years.map((year) => (
                            <option key={year} value={String(year).slice(-2)}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cvv"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="tel"
                          value={field.value}
                          placeholder="CVV"
                          onChange={(event) => handleNumberDigitChange(field.onChange, event, 3)}
                          onKeyDown={(event) => handleNumberKeyDown(field.onChange, event)}
                          className={fieldClass}
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <FormField
              control={form.control}
              name="privacyCheck"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <label className="flex items-center gap-2 text-sm text-[#212121]">
                      <input
                        type="checkbox"
                        checked={Boolean(field.value)}
                        onChange={(event) => field.onChange(event.target.checked)}
                        className="h-5 w-5 accent-[#5896F5]"
                      />
                      <span>
                        <button
                          type="button"
                          className="underline"
                          onClick={() => setPrivacyDialogOpen(true)}
                        >
                          Satış sözleşmesini
                        </button>{' '}
                        okudum ve kabul ediyorum.
                      </span>
                    </label>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
          </div>
          <div className="rounded-2xl border border-[#E7E4DC] bg-white p-3">
            <div className="flex items-start gap-2 rounded-xl bg-[#F6F5F1] px-3 py-3">
              <img src="/checkout/checkout_shield_check.png" alt="" className="mt-0.5 h-5 w-5" />
              <p className="text-xs leading-5 text-[#212121]">
                Kart bilgileriniz{' '}
                <img
                  src="/checkout/checkout_mastercard.png"
                  alt=""
                  className="inline h-4 w-6 object-contain align-text-bottom"
                />{' '}
                <span className="font-semibold">masterpass</span> altyapısında saklanır,
                HızlıÇiçek&apos;te tutulmaz.
              </p>
            </div>
          </div>
          <button
            type="submit"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#2B3280] text-[15px] font-semibold text-white"
          >
            {addingCard ? (
              <Spinner className="size-5 text-white" />
            ) : (
              <img
                src="/checkout/checkout_pay_lock.png"
                alt=""
                className="h-4 w-4 brightness-0 invert"
              />
            )}
            {paymentActionLabel || 'Güvenli Ödeme Yap'}
          </button>
        </form>
        <PrivacyDialog open={isPrivacyDialogOpen} onOpenChange={setPrivacyDialogOpen} />
      </Form>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {!forPayment && (
          <FormField
            control={form.control}
            name="cardAlias"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Kart İsmi"
                    className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="fullname"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Kart Sahibi"
                  className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="cardNumber"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="tel"
                  value={field.value}
                  placeholder="Kart Numarası (**** **** **** ****)"
                  onChange={(e) => {
                    handleCreditCardNumberChange((value) => {
                      field.onChange(value);
                      onCardNumberChange?.(value);
                    }, e);
                  }}
                  onKeyDown={(e) => {
                    handleCreditCardNumberKeyDown(field.onChange, e);
                  }}
                  className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm rounded-sm"
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        <div className="flex flex-row justify-between gap-4">
          <FormField
            control={form.control}
            name="expirationMonth"
            render={({ field }) => (
              <FormItem className="space-y-0 w-full">
                <FormControl>
                  <Input
                    type="tel"
                    value={field.value}
                    placeholder="Ay (**)"
                    onChange={(e) => {
                      handleNumberDigitChange(field.onChange, e, 2);
                    }}
                    onKeyDown={(e) => {
                      handleNumberKeyDown(field.onChange, e);
                    }}
                    className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm rounded-sm"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="expirationYear"
            render={({ field }) => (
              <FormItem className="space-y-0 w-full">
                <FormControl>
                  <Input
                    type="tel"
                    value={field.value}
                    placeholder="Yıl (20**)"
                    onChange={(e) => {
                      handleNumberDigitChange(field.onChange, e, 2);
                    }}
                    onKeyDown={(e) => {
                      handleNumberKeyDown(field.onChange, e);
                    }}
                    className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm rounded-sm"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="cvv"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  type="tel"
                  value={field.value}
                  placeholder="CVV"
                  onChange={(e) => {
                    handleNumberDigitChange(field.onChange, e, 3);
                  }}
                  onKeyDown={(e) => {
                    handleNumberKeyDown(field.onChange, e);
                  }}
                  className="h-[44px] border-hzc_beige_300 placeholder:text-hzc_beige_600 placeholder:font-normal placeholder:text-sm rounded-sm"
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="privacyCheck"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="flex flex-row gap-2">
                  <Checkbox
                    id="privacyCheckbox"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <span className="text-sm text-hzc_beige_700">
                    <span
                      className="font-medium hover:underline hover:cursor-pointer text-[#01B0EF]"
                      onClick={() => setPrivacyDialogOpen(true)}
                    >
                      Satış Sözleşmesi
                    </span>
                    &apos;<label htmlFor="privacyCheckbox">ni okudum ve kabul ediyorum.</label>
                  </span>
                </div>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        {!userIsAnonymous && (
          <FormField
            control={form.control}
            name="termsCheck"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex flex-row gap-2">
                    <Checkbox
                      id="termsCheckbox"
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                    />
                    <span className="text-sm text-hzc_beige_700">
                      <a
                        className="font-medium hover:underline hover:cursor-pointer text-[#01B0EF]"
                        href="https://www.masterpassturkiye.com/terms-and-conditions"
                      >
                        Kullanım koşullarını
                      </a>
                      &nbsp;
                      <label htmlFor="termsCheckbox">
                        okudum, kartımı Masterpass’e kaydetmek istiyorum.
                      </label>
                    </span>
                  </div>
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        )}

        <div
          className={`flex flex-row gap-4 ${addingCard ? 'justify-end' : 'justify-between'} w-full`}
        >
          {!addingCard && !forPayment && (
            <Button
              className=" bg-gray-500 hover:bg-gray-500 transition-all duration-300 hover:scale-105 rounded-full h-[48px] gap-2"
              onClick={() => {
                setPaying(false);
                onOpenChange(false);
              }}
              type="button"
            >
              Kapat
            </Button>
          )}
          <Button
            type="submit"
            disabled={Boolean(paying || addingCard)}
            className={`${addingCard ? ' bg-gray-500 hover:bg-gray-500' : 'bg-hzc_purple_500 hover:bg-hzc_purple_500 transition-all hover:scale-105 duration-300'} rounded-full h-[48px] gap-2`}
          >
            {(addingCard || paying) && <Spinner className="size-6 text-white" />}
            {paying
              ? paymentActionLabel === 'Randevu Al'
                ? 'Randevu alınıyor...'
                : 'Ödeme yapılıyor...'
              : forPayment
                ? (paymentActionLabel ?? (userIsAnonymous ? 'Ödeme Yap' : 'Kaydet ve Öde'))
                : 'Kaydet'}
          </Button>
        </div>
      </form>
      <PrivacyDialog open={isPrivacyDialogOpen} onOpenChange={setPrivacyDialogOpen} />
    </Form>
  );
}
